<template>
  <!--
    Espaço de anúncio do Montilla. A altura do banner que ele substitui já vem
    reservada no HTML estático (`reservedStyle`), então a peça chegando não
    empurra o conteúdo. Quando o SDK assume (ele reserva a mesma altura no
    `render`), a reserva do HTML sai: sem peça, o SDK recolhe a caixa e a leitura
    não fica com buraco. Os `data-montilla-*` seguem no DOM para a extensão e
    para o `ads.inspect()`.
  -->
  <div
    class="montilla-slot"
    :class="{ 'montilla-slot--reserved': reserved && !!reservedStyle }"
    :style="reservedStyle"
    :data-montilla-slot="slug"
    :data-montilla-tags="tagsAttribute"
  />
</template>

<script>
import { MOBILE_BREAKPOINT, MONTILLA_SLOT_HEIGHTS } from '~/utils/montilla-slot-heights'
import { DEFAULT_SLOT_TAGS } from '~/utils/montilla-slot-tags'

export default {
  name: 'MontillaSlot',

  props: {
    slug: {
      type: String,
      required: true,
    },
    tags: {
      type: Array,
      default: () => DEFAULT_SLOT_TAGS,
    },
  },

  data () {
    return { reserved: true }
  },

  computed: {
    tagsAttribute () {
      return this.tags.join(',')
    },

    heights () {
      return MONTILLA_SLOT_HEIGHTS[this.slug]
    },

    // Variáveis de CSS (e não `min-height` inline) porque a altura muda no corte
    // de 768px e o HTML estático não sabe a viewport.
    reservedStyle () {
      if (!this.heights) return undefined

      return {
        '--montilla-slot-height-mobile': this.heights.mobile,
        '--montilla-slot-height-desktop': this.heights.desktop,
      }
    },
  },

  watch: {
    // Navegação entre artigos: se o Nuxt reaproveitar esta instância, as tags
    // mudam e o slot precisa pedir a peça do artigo novo.
    tagsAttribute () {
      this.render()
    },
  },

  mounted () {
    this.render()
  },

  beforeDestroy () {
    this.unmount()
  },

  methods: {
    // O SDK não observa o DOM: em navegação SPA o slot novo nasce vazio, então
    // cada instância pinta a si mesma ao montar (o scan de boot está desligado
    // em plugins/montilla.client.js para não pedir /ads duas vezes).
    // Anúncio nunca pode quebrar a página: qualquer falha vira "nada a exibir".
    async render () {
      try {
        await this.$montilla?.ads?.render(this.$el, {
          slot: this.slug,
          tags: this.tags,
          // Mesma altura da reserva do HTML, escolhida pela viewport (o
          // `render` imperativo não lê os `data-montilla-height*`).
          height: this.currentHeight(),
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[montilla] slot render failed:', this.slug, error)
      } finally {
        // O SDK já reservou (ou recolheu) a caixa: a reserva do HTML sai.
        this.reserved = false
      }
    },

    currentHeight () {
      if (!this.heights) return undefined

      const isDesktop = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT}px)`).matches

      return isDesktop ? this.heights.desktop : this.heights.mobile
    },

    unmount () {
      try {
        this.$montilla?.ads?.unmount(this.$el)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[montilla] slot unmount failed:', this.slug, error)
      }
    },
  },
}
</script>

<style lang="scss">
.montilla-slot--reserved {
  min-height: var(--montilla-slot-height-mobile);

  @media (min-width: 768px) {
    min-height: var(--montilla-slot-height-desktop);
  }
}
</style>
